import { useBillingMeQuery } from '~/api/queries/billing';
import type { Entitlements, SubscriptionPlan, SubscriptionInterval } from '~/utils/billing/entitlements';
import { FREE_CHAT_LIMIT, FREE_MARKETING_LIMIT } from '~/utils/billing/entitlements';
import { TIER_LABEL, canMapFor } from '~/utils/billing/featureTiers';

const ACTIVE_STATUSES = new Set(['active', 'trialing']);

// Fallback while billing state loads / for signed-out users: the Preview tier.
const DEFAULT_ENTITLEMENTS: Entitlements = {
  hasFullMasterclass: false,
  unlimitedTools: false,
  fastPayoutEveryDeal: false,
  freeChatLimit: FREE_CHAT_LIMIT,
  freeMarketingLimit: FREE_MARKETING_LIMIT,
  planLabel: 'Basic plan',
  tier: 'PREVIEW',
  tierLabel: TIER_LABEL.PREVIEW,
  can: canMapFor('PREVIEW'),
  uaeAccessLevel: null,
};

export const useEntitlements = () => {
  const { data } = useBillingMeQuery();

  const entitlements = computed<Entitlements>(() => data.value?.entitlements ?? DEFAULT_ENTITLEMENTS);
  const planLabel = computed(() => entitlements.value.planLabel);
  const plan = computed<SubscriptionPlan | null>(() => data.value?.subscriptionPlan ?? null);
  const interval = computed<SubscriptionInterval | null>(() => data.value?.subscriptionInterval ?? null);
  const status = computed<string | null>(() => data.value?.subscriptionStatus ?? null);
  const provider = computed<string | null>(() => data.value?.subscriptionProvider ?? null);
  const cancelAtPeriodEnd = computed<boolean>(() => data.value?.subscriptionCancelAtPeriodEnd ?? false);
  // ID-181: a seat in a live agency workspace counts as paid; the agency is the payer.
  const agencySeat = computed<{ agencyName: string; role: string } | null>(() => data.value?.b2bSeat ?? null);
  const isPaid = computed(
    () => !!agencySeat.value || (plan.value !== null && ACTIVE_STATUSES.has(String(status.value ?? ''))),
  );
  const isTeamOwner = computed<boolean>(() => data.value?.isTeamOwner ?? false);
  const isTeamMember = computed<boolean>(() => data.value?.team?.role === 'MEMBER');
  const teamOwnerName = computed<string | null>(() => data.value?.team?.ownerName ?? null);
  // Trial state (no-card signup-code trial). `status === 'trialing'` drives it;
  // trialEndsAt gives the countdown for the banner.
  const trialEndsAt = computed<string | null>(() => (data.value?.trialEndsAt as string | null) ?? null);
  const isTrialing = computed<boolean>(() => String(status.value ?? '') === 'trialing');
  // Master paywall flag — dormant (false) until B7 flips it. The tier gate reads
  // this so locks appear only once enforcement is live.
  const tierGatingEnabled = computed<boolean>(() => data.value?.tierGatingEnabled ?? false);
  // Plan billed by the App Store / Google Play: managed there, never through Stripe/PayPal.
  const storeManagedBy = computed<'app_store' | 'google_play' | null>(() => {
    const by = data.value?.store?.managedBy;
    return by === 'app_store' || by === 'google_play' ? by : null;
  });
  const storeManageUrl = computed<string | null>(() => data.value?.store?.manageUrl ?? null);

  return {
    entitlements,
    planLabel,
    plan,
    interval,
    status,
    provider,
    cancelAtPeriodEnd,
    isPaid,
    agencySeat,
    isTrialing,
    trialEndsAt,
    isTeamOwner,
    isTeamMember,
    teamOwnerName,
    tierGatingEnabled,
    storeManagedBy,
    storeManageUrl,
  };
};
